import React, { useEffect, useState } from "react";
import { Sparkles, TrendingUp, TrendingDown, Minus, RefreshCw, ArrowRight } from "lucide-react";
import { fetchRecommendations } from "@/lib/forgeApi";

// Strategy of the Day — a passive card on /forge that fetches the
// recommender for the current ticker and surfaces the top-ranked
// template along with a one-line rationale. Tap "Apply" to load it as
// the active strategy via the parent's `onApply` handler.
//
// Props:
//   ticker:  current symbol
//   onApply: (template) => void  — receives the full recommendation row
//                                 (template_id, default_dte, default_width_pct, etc)
const trendIcon = (trend) => {
  if (trend === "bullish") return <TrendingUp className="w-3 h-3 text-[#22C55E]" />;
  if (trend === "bearish") return <TrendingDown className="w-3 h-3 text-[#EF4444]" />;
  return <Minus className="w-3 h-3 text-white/55" />;
};

const StrategyOfDayCard = ({ ticker = "SPY", onApply = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    fetchRecommendations(ticker, 3)
      .then((d) => setData(d))
      .catch((e) => setErr(e?.message || "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ticker]);

  if (loading) {
    return (
      <div
        className="bg-gradient-to-br from-[#0E1A30] to-[#13203A] border border-[#A98CFF]/25 rounded-xl px-5 py-4"
        data-testid="forge-strategy-of-day-loading"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C4B5FD]">
          <Sparkles className="w-3.5 h-3.5 animate-pulse" /> Strategy of the Day
        </div>
        <div className="text-[11px] text-white/45 mt-2">Reading current market regime…</div>
      </div>
    );
  }

  if (err || !data?.recommendations?.length) {
    return (
      <div
        className="bg-[#0E1A30] border border-white/10 rounded-xl px-5 py-4 flex items-center justify-between gap-3"
        data-testid="forge-strategy-of-day-empty"
      >
        <div className="flex items-center gap-2 text-[11px] text-white/55">
          <Sparkles className="w-3.5 h-3.5 text-[#C4B5FD]" />
          Couldn't compute a recommendation right now.
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="forge-strategy-of-day-retry"
          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em] px-2.5 py-1 rounded border border-white/15 text-white/70 hover:text-white hover:border-white/35 transition"
        >
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  const top = data.recommendations[0];
  const profile = data.profile || {};

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-[#0E1A30] via-[#15243F] to-[#1B2A4A] border border-[#A98CFF]/30 rounded-xl px-5 py-4 shadow-[0_0_40px_-20px_#A98CFF]"
      data-testid="forge-strategy-of-day"
    >
      <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-[#A98CFF]/10 blur-3xl" aria-hidden="true" />

      <div className="flex items-start gap-4 relative">
        {/* Left: Pick */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-[#C4B5FD]">
            <Sparkles className="w-3 h-3" /> Strategy of the Day · {data.symbol}
          </div>
          <div className="mt-1.5 flex items-center gap-2 flex-wrap">
            <span
              data-testid="forge-strategy-of-day-name"
              className="text-[20px] font-medium text-white tracking-tight"
            >
              {top.template_name}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-white/15 text-white/65">
              {top.category}
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[#22C55E]/30 text-[#86EFAC] bg-[#22C55E]/10">
              score {Math.round(top.score)}
            </span>
          </div>

          {/* Rationale */}
          <ul className="mt-2.5 space-y-1">
            {(top.rationale || []).slice(0, 3).map((r, i) => (
              <li key={i} className="text-[11.5px] text-white/65 inline-flex items-start gap-1.5">
                <span className="text-[#A98CFF] mt-0.5">·</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>

          {/* Profile readout */}
          <div className="mt-3 flex items-center gap-3 flex-wrap text-[10.5px] text-white/55">
            <span className="inline-flex items-center gap-1">
              {trendIcon(profile.trend)} <b className="text-white/85 capitalize">{profile.trend || "—"}</b> trend
            </span>
            <span className="text-white/25">·</span>
            <span>IV30 <b className="text-white/85 num">{(profile.iv30_pct ?? 0).toFixed(1)}%</b></span>
            <span className="text-white/25">·</span>
            <span>RV30 <b className="text-white/85 num">{(profile.vol30_pct ?? 0).toFixed(1)}%</b></span>
            <span className="text-white/25">·</span>
            <span>RSI <b className="text-white/85 num">{(profile.rsi ?? 0).toFixed(0)}</b></span>
            {Number.isFinite(profile.earnings_days) && (
              <>
                <span className="text-white/25">·</span>
                <span>earnings in <b className="text-[#FACC15] num">{profile.earnings_days}d</b></span>
              </>
            )}
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <button
            type="button"
            onClick={() => onApply?.(top)}
            data-testid="forge-strategy-of-day-apply"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3.5 py-2 rounded-md bg-[#A98CFF] hover:bg-[#9B7AF5] text-white font-medium transition"
          >
            Apply <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={load}
            data-testid="forge-strategy-of-day-refresh"
            className="text-[9.5px] uppercase tracking-[0.18em] px-2 py-1 rounded text-white/50 hover:text-white transition inline-flex items-center gap-1"
          >
            <RefreshCw className="w-2.5 h-2.5" /> Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default StrategyOfDayCard;
